import axiosInstance from '../api';

/**
 * A project as the /manage/projects API returns it.
 *
 * `name`/`description` are the shape some responses use; `projectName`/
 * `projectDescription` are the shape the rest use. Both are carried because
 * the API returns either depending on the endpoint, and callers read through
 * `projectLabel` rather than picking one.
 */
export interface Project {
    _id: string;
    projectName: string;
    projectDescription?: string;
    lastUpdatedDate?: string;
    name?: string;
    description?: string;
}

/** localStorage key holding the project the user last worked in. */
const STORED_PROJECT_KEY = 'default_project_id';

/** How many projects the workspace selector offers. Matches the hub's own page size. */
const PAGE_LIMIT = 10;

export const projectLabel = (project: Project): string =>
    project.name || project.projectName || 'Untitled project';

/**
 * The in-flight or settled list request, keyed by the org it was made for.
 *
 * Both the landing resolver and the workspace selector want this list on first
 * paint. Sharing the promise means one request rather than two, and a switch
 * of org (sign out, sign in as someone else) misses the cache by key.
 */
let cached: { orgId: string; projects: Promise<Project[]> } | null = null;

const currentOrgId = (): string | null => localStorage.getItem('org_id');

/**
 * The org's projects, most recent page only.
 *
 * Pass `force` after creating a project, which the cache cannot know about.
 */
export const listProjects = async (opts?: { force?: boolean }): Promise<Project[]> => {
    const orgId = currentOrgId();
    if (!orgId) return [];

    if (opts?.force || !cached || cached.orgId !== orgId) {
        const request = axiosInstance
            .get(`/projects?orgId=${orgId}&page=1&limit=${PAGE_LIMIT}`)
            .then((response) => response.data?.projects ?? [])
            .catch((error) => {
                // A failed list must not poison the cache — the next caller
                // should be able to retry rather than inherit the rejection.
                cached = null;
                throw error;
            });
        cached = { orgId, projects: request };
    }

    return cached.projects;
};

export const getStoredProjectId = (): string | null =>
    localStorage.getItem(STORED_PROJECT_KEY);

export const setStoredProjectId = (projectId: string): void => {
    localStorage.setItem(STORED_PROJECT_KEY, projectId);
};

/**
 * Drops the memoized list, keeping the remembered project.
 *
 * Called after creating a project, which the cache cannot know about but which
 * is about to become the remembered one.
 */
export const invalidateProjectList = (): void => {
    cached = null;
};

/**
 * Drops both the memoized list and the remembered project.
 *
 * Called on sign-out: `clearStoredAuthTokens` only removes keys matching
 * /token|auth/i, so without this the next user on the machine would land on
 * the previous user's project.
 */
export const clearProjectCache = (): void => {
    cached = null;
    localStorage.removeItem(STORED_PROJECT_KEY);
};

/**
 * Which project to open when the user has not named one.
 *
 * The one they last worked in, as long as it is still there — a project that
 * has since been deleted, or that belongs to an org they no longer have, falls
 * back to the first of the list. Null means the org has no projects at all,
 * which is the caller's cue to send them somewhere they can create one.
 */
export const resolveDefaultProjectId = async (): Promise<string | null> => {
    const projects = await listProjects();
    if (!projects.length) return null;

    const stored = getStoredProjectId();
    if (stored && projects.some((project) => project._id === stored)) return stored;

    return projects[0]._id ?? null;
};
